import { useId } from "react";
import { Lock } from "@phosphor-icons/react";
import {
  useClientPublishSiteFacts,
  useClientSaveSiteFactsDraft,
  useClientSiteFacts,
  type ClientFactsSite,
  type ClientSiteFacts
} from "../../data/clientFactsQueries";
import { describeValue } from "../../lib/facts/words";
import { BoxState } from "../../ui/BoxState";
import { Callout } from "../../ui/Callout";
import { Panel } from "../../ui/Panel";
import { formatWhen } from "../clientOps/format";
import { ClientFactsForm } from "./ClientFactsForm";
import { changedLine, editorsLine, publishedByName } from "./clientFactsWords";
import { useViewOnly } from "../../data/readSource";

/**
 * One website on Webbplatsens fakta, build round 19 (2026-10-08): its facts, editable or read only
 * as the server says, and its versions.
 *
 * `can_edit` is the server's word, the same rule its two write gateways hold: an active authorised
 * contact of a company whose management is open for writing. Everybody else, a member or a contact
 * while the portal is in Läsläge, reads the published facts and one line naming who can change them,
 * decided by the planning chat on 2026-10-08. The interface follows the read and adds no rule of its
 * own; the gateways refuse a write on their own whatever this page offers.
 */

/** The published facts as text, for somebody who may not change them. In the schema's order and words. */
function ReadOnlyFacts({ facts }: { facts: ClientSiteFacts }) {
  const values = facts.published.values;

  return (
    <dl className="kv" data-testid="client-facts-readonly">
      {facts.schema.document.fields.map((field) => {
        const value = values[field.key];
        const empty = value === null || value === undefined;
        return (
          <div key={field.key} data-testid="client-facts-value" data-key={field.key}>
            <dt>{field.label}</dt>
            <dd className={empty ? "quiet" : undefined}>{empty ? "Inte ifyllt" : describeValue(value, field)}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** Every published version, newest first: when, who, and which fields changed. */
function Versions({ facts }: { facts: ClientSiteFacts }) {
  const titleId = useId();

  return (
    <Panel title="Versioner" titleId={titleId} flush testId="client-facts-versions">
      {facts.versions.length === 0 ? (
        <BoxState kind="empty" compact title="Ingen version är publicerad än" />
      ) : (
        <ul className="k-rows" aria-labelledby={titleId}>
          {facts.versions.map((version) => (
            <li
              key={version.version}
              className="k-item"
              data-testid="client-facts-version"
              data-version={version.version}
              data-origin={version.origin}
            >
              <span className="primary-cell">
                <strong>Version {version.version}</strong>
                <span>
                  {formatWhen(version.published_at)} · {publishedByName(version.origin, version.published_by)}
                </span>
                <span data-testid="client-facts-version-changed">{changedLine(version.changed)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function FactsForSite({ tenantId, site }: { tenantId: string; site: ClientFactsSite }) {
  const viewOnly = useViewOnly();
  const facts = useClientSiteFacts(tenantId, site.service_id);
  const save = useClientSaveSiteFactsDraft();
  const publish = useClientPublishSiteFacts();
  const titleId = useId();

  if (!facts.data) {
    if (facts.isError && facts.error.code === "PORTAL_SITE_FACTS_NOT_PUBLISHED") {
      // The list said there were facts and the site says there are none: it changed in between.
      // Calm, because there is nothing for the client to do about it.
      return (
        <section className="panel">
          <BoxState kind="empty" title="Här finns inget att ändra än" body={facts.error.message} />
        </section>
      );
    }
    return (
      <section className="panel">
        {facts.isError ? (
          <BoxState
            kind="error"
            title="Uppgifterna gick inte att hämta"
            body={`${facts.error.message} Det betyder inte att webbplatsen saknar uppgifter.`}
            onRetry={() => void facts.refetch()}
          />
        ) : (
          <BoxState kind="loading" title="Hämtar uppgifterna" />
        )}
      </section>
    );
  }

  const data = facts.data;

  return (
    <>
      {facts.isError ? (
        <Callout tone="warn" testId="client-facts-reread-failed">
          Den senaste hämtningen gick inte fram, så det här är vad sidan visste innan.
        </Callout>
      ) : null}
      {data.can_edit ? null : (
        <Callout icon={Lock} testId="client-facts-editors">
          {editorsLine(data.editors)}
        </Callout>
      )}
      <Panel title={data.name} titleId={titleId} testId="client-facts-site">
        {data.can_edit && !viewOnly ? (
          <ClientFactsForm
            facts={data}
            tenantId={tenantId}
            serviceId={site.service_id}
            save={save}
            publish={publish}
          />
        ) : (
          <ReadOnlyFacts facts={data} />
        )}
      </Panel>
      <Versions facts={data} />
    </>
  );
}
